/*
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// TWEEN
import TWEEN from '@tweenjs/tween.js';

// MOBILE
import isMobile from 'is-mobile-with-ipad';

// APP
import AppBase from './AppBase';

// HUD
import HUD from '../hud/HUD';
import TouchRenderer from '../gfx/TouchRenderer';

// AR
import ARModelPlacer from '../ar/ARModelPlacer';
import ARControls from '../ar/ARControls';
import ARModelDimensions from '../ar/ARModelDimensions';
import ARHands from '../ar/ARHands';

// DOM
import DOMFadeable from '../html/DOMFadeable';

// CONSTANTS
import * as Constants from '../constants/Constants';

let tempModelPosition = new THREE.Vector3();
let tempCameraPosition = new THREE.Vector3();

export default class App extends AppBase {
  constructor(props) {
    super(props);
    this._setup(props);
  }

  setup = props => {
    this.setupProperties(props);
    this.setupHTMLButtons();
    this.setupScene();
    this.setupHUD();
    this.setupARHands();
    this.setupTouchRenderer();
    this.setupEvents();
    this.setupContent(props);
    this.resized();
  };

  setupARHands = () => {
    this.arHands = new ARHands({
      renderer: this.renderer,
      bindFbo: this._bindSessionFbo,
      unbindFbo: this._unbindSessionFbo,
    });
  };

  setupProperties = props => {
    this.debug = props.debug != undefined ? props.debug : false;
    this.placedContent = false;
    this.duration = Constants.duration;
    this.showTouches = props.showTouches;
    this.offsetAngle = Math.PI * 0.25;
    this.enterARImmediate = props.ar;
    this.content = props.scene;

    this.content.setRenderer(this.renderer);
    this.content.setCanvas(this.canvas);
  };

  setupHTMLButtons = () => {
    this.setupEnterARButton();
    this.setupExitARButton();
    this.setupEnterFSButton();
    this.setupExitFSButton();
  };

  setupScene = () => {
    this.scene = new THREE.Scene();
  };

  setupEnterARButton = () => {
    this.enterARButtonElement = document.getElementById('enter-ar-button');
    this.enterARButtonElement.style.opacity = 0.0;
    this.enterARButton = new DOMFadeable({
      element: this.enterARButtonElement,
    });

    this.enterARButtonElement.addEventListener('click', () => {
      if (this.hasAR()) {
        this.enterARButton.fadeOut(0.0);
        this.enableAR();
      } else {
        // I'm not sure when this can be called
        throw new Error('Platform does not support AR');
      }
    });
  };

  setupExitARButton = () => {
    this.exitARButtonElement = document.getElementById('exit-ar-button');
    this.exitARButtonElement.style.opacity = 0.0;
    this.exitARButton = new DOMFadeable({
      element: this.exitARButtonElement,
    });
    this.exitARButtonElement.addEventListener('click', () => {
      this.disableAR();
    });
  };

  setupEnterFSButton = () => {
    this.enterFSButtonElement = document.getElementById('enter-fs-button');
    this.enterFSButtonElement.style.opacity = 0.0;
    this.enterFSButton = new DOMFadeable({
      element: this.enterFSButtonElement,
    });

    this.enterFSButtonElement.addEventListener('click', () => {
      this.enterFullscreen();
    });
  };

  setupExitFSButton = () => {
    this.exitFSButtonElement = document.getElementById('exit-fs-button');
    this.exitFSButtonElement.style.opacity = 0.0;
    this.exitFSButton = new DOMFadeable({
      element: this.exitFSButtonElement,
    });

    this.exitFSButtonElement.addEventListener('click', () => {
      this.exitFullscreen();
    });
  };

  setupHUD = () => {
    this.HUD = new HUD({
      canvas: this.canvas,
      renderer: this.renderer,
      duration: this.duration,
      spacing: isMobile() ? 48 : 24,
      debug: this.debug,
    });

    this.HUD.resize();
  };

  setupContent = props => {
    this.content.setup(props);
  };

  contentLoaded = event => {
    console.log('content loaded');
    this.HUD.hideLoadingIndicator();
    if (this.enterARImmediate) {
      this.enableAR();
    } else {
      this.content.enter(this.duration, 0, () => {
        if (this.hasAR()) {
          this.enterARButton.fadeIn(0.0);
        } else {
          this.enterFSButton.fadeIn(0.0);
        }
      });
    }
  };

  enterAR = () => {
    console.log('enter AR');
    this.HUD.enterAR();
    this.setupScene();
    this.setupARModelPlacer();
    this.setupARControls();
    this.setupARModelDimensions();
    this.content.exit(this.duration, 0, () => {
      this.renderer.domElement.style.zIndex = 10;
      this.exitARButton.fadeIn(0.0);
      this.setAR(true);
    });
    this.enterFullscreen();
  };

  exitAR = () => {
    console.log('exit AR');
    this.HUD.exitAR();
    this.HUD.dismissToasts(this.duration);
    this.exitARButton.fadeOut(0.0);
    this.arModelPlacer.fadeOut(this.duration, 0);
    this.arHands.scaleOut(this.duration, 0);
    this.content.exitAR(this.duration, 0, () => {
      this.exitFullscreen();
      this.renderer.domElement.style.zIndex = -1;
      this.content.reset();
      this.arControls.disable();
      this.setAR(false);
      this.placedContent = false;
    });
  };

  enteredFullscreen = event => {
    console.log('entered Fullscreen');
    this.container.classList.add('fullscreen');
    this.container.classList.remove('container');
    this.renderer.setSize(screen.width, screen.height);
    this.resized();
  };

  exitedFullscreen = event => {
    console.log('exited Fullscreen');
    if (this.hasAR()) {
      this.disableAR();
      this.enterARButton.fadeIn(0.0);
    } else {
      this.exitFSButton.fadeOut(0.0);
      this.enterFSButton.fadeIn(0.0);

      this.container.classList.add('container');
      this.container.classList.remove('fullscreen');
      this.canvasRect = this.container.getBoundingClientRect();
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.setSize(this.canvasRect.width, this.canvasRect.height);
      this.content.reset();
    }
    this.resized();
  };

  enteredAR = event => {
    console.log('entered AR');
    if (this.arModelPlacer.getTrackingState()) {
      this.arModelPlacer.fadeIn(this.duration);
      this.HUD.findingSurface();
    } else {
      this.arHands.scaleIn(this.duration, 0.0);
      this.HUD.foundSurface();
    }
  };

  exitedAR = event => {
    console.log('exited AR');
    this.content.enter(this.duration, 0, () => {
      this.enterARButton.fadeIn(0.0);
    });
  };

  resized = () => {
    this.arHands.resize();
    this.content.resize();
    this.HUD.resize();
    if (this.touchRenderer) this.touchRenderer.resize();
  };

  setupEvents = () => {
    this.addEventListener('enteredFullscreen', this.enteredFullscreen);
    this.addEventListener('exitedFullscreen', this.exitedFullscreen);

    this.addEventListener('enableAR', this.enterAR);
    this.addEventListener('disableAR', this.exitAR);

    this.addEventListener('enteredAR', this.enteredAR);
    this.addEventListener('exitedAR', this.exitedAR);

    this.addEventListener('down', this.HUD.down);
    this.addEventListener('up', this.HUD.up);

    this.addEventListener('modelPlaced', this.HUD.modelPlaced);
    this.addEventListener('modelDragged', this.HUD.modelDragged);
    this.addEventListener('modelRotated', this.HUD.modelRotated);

    this.addEventListener('resized', this.resized);

    this.content.addEventListener('contentLoaded', this.contentLoaded);
    this.canvas.addEventListener('click', this.onClick, false);
  };

  onClick = event => {
    if (this.IsAR() && !this.placedContent && !event.defaultPrevented) {
      if (this.arModelPlacer.getTrackingState()) {
        let position = this.arModelPlacer.getPosition();
        if (!this.placedContent) {
          this.placedContent = true;
          this.content.position.set(position.x, position.y, position.z);
          this.content.updateMatrixWorld(true);

          let model = this.content.getModel();
          model.getWorldPosition(tempModelPosition);
          this.camera.getWorldPosition(tempCameraPosition);

          let x = tempCameraPosition.x - tempModelPosition.x;
          let z = tempCameraPosition.z - tempModelPosition.z;
          let angle = Math.atan2(x, z);
          model.rotation.set(0, angle + this.offsetAngle, 0);
          model.updateMatrixWorld(true);

          this.arHands.scaleOut(this.duration);
          this.arModelPlacer.fadeOut(this.duration);
          this.content.enterAR(this.duration, 0, () => {
            this.content.dispatchEvent({
              type: 'cameraChanged',
              object: this.camera,
            });
            this.placedContent = true;
            this.arControls.enable();
            this.dispatchEvent({ type: 'modelPlaced', object: this });
            this.dispatchEvent({ type: 'up', object: this });
          });
        }
      }
    }
  };

  setupARModelPlacer = () => {
    let size = new THREE.Vector3();
    this.content.getBoundingBox().getSize(size);
    this.arModelPlacer = new ARModelPlacer({
      xrSession: this.xrSession,
      frameOfRef: this.frameOfRef,
      renderer: this.renderer,
      camera: this.camera,
      boundingBox: this.content.getBoundingBox(),
      color: new THREE.Color(0xffffff),
      opacity: 0.75,
      padding: 0.05,
      radius: 0.2,
      easing: 0.75,
      offsetAngle: this.offsetAngle,
    });

    this.scene.add(this.arModelPlacer);

    this.arModelPlacer.addEventListener('findingSurface', event => {
      if (this.IsAR()) {
        this.HUD.findingSurface(event);
      }
    });

    this.arModelPlacer.addEventListener('foundSurface', event => {
      if (this.IsAR()) {
        this.HUD.foundSurface(event);
        if (this.arHands.visible) {
          this.arHands.scaleOut(this.duration);
          this.arModelPlacer.fadeIn(this.duration);
        }
      }
    });
  };

  setupTouchRenderer = () => {
    if (this.showTouches && isMobile()) {
      this.touchRenderer = new TouchRenderer({
        canvas: this.canvas,
        renderer: this.renderer,
      });
    }
  };

  setupARControls = () => {
    this.arControls = new ARControls({
      xrSession: this.xrSession,
      frameOfRef: this.frameOfRef,
      scene: this.scene,
      model: this.content.getModel(),
      boundingBox: this.content.getBoundingBox(),
      camera: this.camera,
      renderer: this.renderer,
      canvas: this.canvas,
      debug: this.debug,
      scale: false,
      rotate: true,
      translate: true,
      useHitTest: false,
    });

    this.arControls.addEventListener('down', () => {
      if (this.IsAR()) {
        this.dispatchEvent({ type: 'down', object: this });
      }
    });

    this.arControls.addEventListener('up', () => {
      if (this.IsAR()) {
        this.dispatchEvent({ type: 'up', object: this });
      }
    });

    this.arControls.addEventListener('onDownPan', () => {
      if (this.IsAR()) {
        this.dispatchEvent({ type: 'modelDragged', object: this });
      }
    });

    this.arControls.addEventListener('onDownRotate', () => {
      if (this.IsAR()) {
        this.dispatchEvent({ type: 'modelRotated', object: this });
      }
    });
  };

  setupARModelDimensions = () => {
    this.arModelDimensions = new ARModelDimensions({
      model: this.content.getModel(),
      boundingBox: this.content.getBoundingBox(),
      controls: this.arControls,
      color: new THREE.Color(0xffffff),
      opacity: 0.5,
      padding: 0.05,
      radius: 0.2,
    });
    this.scene.add(this.arModelDimensions);
  };

  update = (time, delta) => {
    if (this.IsAR()) {
      if (!this.placedContent) {
        this.arModelPlacer.update();
      }
      if (this.arControls) {
        this.arControls.update(time);
      }
      if (this.arHands) {
        this.arHands.update(time, delta);
      }
    }

    this.content.update(time);
    TWEEN.update(time);
    if (this.HUD) {
      this.HUD.update(time);
    }
  };

  render = (camera, frame) => {
    if (!this.IsAR()) {
      this.renderer.clear(true, true);
    } else {
      this.renderer.render(this.scene, camera);
    }

    this.content.render(camera, frame);
    if (this.arHands) {
      this.arHands.render();
    }

    this.HUD.render();
    if (this.touchRenderer) this.touchRenderer.render();
  };
}



// WEBPACK FOOTER //
// ./src/app/App.js